import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import {PosterEditorItemAdjustmentOptions} from '@Panels/poster-editor-item-adjustments-panel/components/default-adjustment-options/default-adjustment-options.types';
import {ContextualMenu} from '@Components/contextual-menu';
import {ImageBgBrightnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-bg-brightness-option';
import {ImageBgContrastOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-bg-contrast-option';
import {ImageBgVibranceOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-bg-vibrance-option';
import {ImageBgSaturationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-bg-saturation-option';
import {BaseTintAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/base-tint-adjustment-option';
import {getCurrentPage, getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {DefaultPosterEditorItemAdjustmentOptions} from '@Panels/poster-editor-item-adjustments-panel/components/default-adjustment-options';
import {BaseGammaAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/base-gamma-adjustment-option';
import type {GammaColorType} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-gamma-option/base-gamma-option.types';
import {BaseMultiplyAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/base-multiply-adjustment-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {useAppSelector} from '@/hooks';

interface ImageBackgroundAdjustmentOptionsProps {
  panelId: string;

  onBack(): void;

  onClose(): void;
}

enum AdjustmentPanels {
  DEFAULT = 'DEFAULT',
  MULTIPLY_COLOR_PICKER = 'MULTIPLY_COLOR_PICKER',
  TINT_COLOR_PICKER = 'COLOR_PICKER',
}

export default function ImageBackgroundAdjustmentOptions({...props}: ImageBackgroundAdjustmentOptionsProps): ReactElement | null {
  const [selectedSubPanel, setSelectedSubPanel] = useState(PosterEditorItemAdjustmentOptions.NONE);
  const [panelToShow, setPanelToShow] = useState(AdjustmentPanels.DEFAULT);
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const currentPageObject = useAppSelector(getCurrentPage);

  useEffect((): void => {
    if (panelToShow === AdjustmentPanels.DEFAULT && selectedSubPanel === PosterEditorItemAdjustmentOptions.NONE) {
      updateOptionsPanelHeight(props.panelId, '158px');
    }
  }, [panelToShow]);

  if (!currentPageObject) {
    return null;
  }

  const onBackButton = (): void => {
    if (selectedSubPanel !== PosterEditorItemAdjustmentOptions.NONE) {
      setSelectedSubPanel(PosterEditorItemAdjustmentOptions.NONE);
      return;
    }
    if (panelToShow === AdjustmentPanels.DEFAULT) {
      props.onBack();
    } else {
      setSelectedSubPanel(PosterEditorItemAdjustmentOptions.NONE);
    }
  };

  const getHeaderText = (): string => {
    switch (selectedSubPanel) {
      case PosterEditorItemAdjustmentOptions.BRIGHTNESS:
        return window.i18next.t('pmwjs_brightness');
      case PosterEditorItemAdjustmentOptions.CONTRAST:
        return window.i18next.t('pmwjs_contrast');
      case PosterEditorItemAdjustmentOptions.GAMMA:
        return window.i18next.t('pmwjs_gamma');
      case PosterEditorItemAdjustmentOptions.MULTIPLY:
        return window.i18next.t('pmwjs_multiply');
      case PosterEditorItemAdjustmentOptions.SATURATION:
        return window.i18next.t('pmwjs_saturation');
      case PosterEditorItemAdjustmentOptions.TINT:
        return window.i18next.t('pmwjs_tint');
      case PosterEditorItemAdjustmentOptions.VIBRANCE:
        return window.i18next.t('pmwjs_vibrance');
      case PosterEditorItemAdjustmentOptions.NONE:
      default:
        return window.i18next.t('pmwjs_adjustments');
    }
  };

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        onBackButton={onBackButton}
        showBackButton
        onClose={(): void => {
          props.onClose();
        }}
        title={getHeaderText()}
      />
    );
  };

  const getBrightnessOption = (): ReactElement => {
    return <ImageBgBrightnessOption />;
  };

  const getSaturationOption = (): ReactElement => {
    return <ImageBgSaturationOption />;
  };
  const getVibranceOption = (): ReactElement => {
    return <ImageBgVibranceOption />;
  };
  const getContrastOption = (): ReactElement => {
    return <ImageBgContrastOption />;
  };

  const onToggleTint = (): void => {
    if ('imageBackgroundItemObject' in currentPageObject.background.details) {
      void currentPage?.background.updateFromObject({
        details: {
          imageBackgroundItemObject: {
            effects: {
              tint: !currentPageObject.background.details.imageBackgroundItemObject.effects.tint,
            },
          },
        },
      });
    } else {
      throw new Error('imageBackgroundItemObject does not exist in page background details');
    }
  };

  const onUpdateOpacity = (tintOpacity: number, undoable = true): void => {
    if ('imageBackgroundItemObject' in currentPageObject.background.details) {
      const tintColor = currentPageObject.background.details.imageBackgroundItemObject.effects.tintColor.slice() as RGBA;
      tintColor[3] = tintOpacity;
      void currentPage?.background.updateFromObject(
        {
          details: {
            imageBackgroundItemObject: {
              effects: {
                tintColor,
              },
            },
          },
        },
        {undoable}
      );
    } else {
      throw new Error('imageBackgroundItemObject does not exist in page background details');
    }
  };

  const getTintOption = (): ReactElement | null => {
    if (!currentBackgroundObject || currentBackgroundObject.details.type !== BackgroundTypeName.IMAGE) {
      return null;
    }

    return (
      <BaseTintAdjustmentOption
        panelId={props.panelId}
        tint={currentBackgroundObject.details.imageBackgroundItemObject.effects.tint}
        tintColor={currentBackgroundObject.details.imageBackgroundItemObject.effects.tintColor}
        tintOpacity={currentBackgroundObject.details.imageBackgroundItemObject.effects.tintColor[3]}
        onToggleTint={onToggleTint}
        onClickColorFill={(): void => {
          setPanelToShow(AdjustmentPanels.TINT_COLOR_PICKER);
        }}
        updateOpacity={onUpdateOpacity}
      />
    );
  };

  const onToggleGamma = (): void => {
    if ('imageBackgroundItemObject' in currentPageObject.background.details) {
      void currentPage?.background.updateFromObject({
        details: {
          imageBackgroundItemObject: {
            effects: {
              gamma: {
                isEnabled: !currentPageObject.background.details.imageBackgroundItemObject.effects.gamma.isEnabled,
              },
            },
          },
        },
      });
    } else {
      throw new Error('imageBackgroundItemObject does not exist in page background details');
    }
  };

  const updateGammaColors = (color: GammaColorType, updatedValue: number, undoable = true): void => {
    if (currentPage?.background.details.isImage()) {
      void currentPage?.background.updateFromObject(
        {
          details: {
            imageBackgroundItemObject: {
              effects: {
                gamma: currentPage.background.details.imageBackgroundItem.effects.getPartialUpdatedGammaObjectFromParams(updatedValue, color),
              },
            },
          },
        },
        {
          undoable,
        }
      );
    }
  };

  const getGammaOptions = (): ReactElement | null => {
    if (!currentBackgroundObject || currentBackgroundObject.details.type !== BackgroundTypeName.IMAGE) {
      return null;
    }
    return (
      <BaseGammaAdjustmentOption
        panelId={props.panelId}
        gammaObject={currentBackgroundObject.details.imageBackgroundItemObject.effects.gamma}
        onToggleGamma={onToggleGamma}
        updateColor={updateGammaColors}
      />
    );
  };

  const onToggleMultiply = (): void => {
    if ('imageBackgroundItemObject' in currentPageObject.background.details) {
      void currentPage?.background.updateFromObject({
        details: {
          imageBackgroundItemObject: {
            effects: {
              multiply: !currentPageObject.background.details.imageBackgroundItemObject.effects.multiply,
            },
          },
        },
      });
    } else {
      throw new Error('imageBackgroundItemObject does not exist in page background details');
    }
  };

  const onUpdateMultiplyOpacity = (multiplyOpacity: number, undoable = true): void => {
    if ('imageBackgroundItemObject' in currentPageObject.background.details) {
      const multiplyColor = currentPageObject.background.details.imageBackgroundItemObject.effects.multiplyColor.slice() as RGBA;
      multiplyColor[3] = multiplyOpacity;
      void currentPage?.background.updateFromObject(
        {
          details: {
            imageBackgroundItemObject: {
              effects: {
                multiplyColor,
              },
            },
          },
        },
        {undoable}
      );
    } else {
      throw new Error('imageBackgroundItemObject does not exist in page background details');
    }
  };

  const getMultiplyOption = (): ReactElement | null => {
    if (!currentBackgroundObject || currentBackgroundObject.details.type !== BackgroundTypeName.IMAGE) {
      return null;
    }
    return (
      <BaseMultiplyAdjustmentOption
        panelId={props.panelId}
        multiply={currentBackgroundObject.details.imageBackgroundItemObject.effects.multiply}
        multiplyColor={currentBackgroundObject.details.imageBackgroundItemObject.effects.multiplyColor}
        multiplyOpacity={currentBackgroundObject.details.imageBackgroundItemObject.effects.multiplyColor[3]}
        onToggleMultiply={onToggleMultiply}
        onClickColorFill={(): void => {
          setPanelToShow(AdjustmentPanels.MULTIPLY_COLOR_PICKER);
        }}
        updateOpacity={onUpdateMultiplyOpacity}
      />
    );
  };

  const onClickHandler = (clickedId: PosterEditorItemAdjustmentOptions): void => {
    setSelectedSubPanel(clickedId);
  };

  const getDefaultContent = (): ReactElement => {
    return <DefaultPosterEditorItemAdjustmentOptions isForBackground panelId={props.panelId} onClick={onClickHandler} />;
  };

  const getContentFromState = (): ReactElement | null => {
    switch (selectedSubPanel) {
      case PosterEditorItemAdjustmentOptions.BRIGHTNESS:
        return getBrightnessOption();
      case PosterEditorItemAdjustmentOptions.SATURATION:
        return getSaturationOption();
      case PosterEditorItemAdjustmentOptions.VIBRANCE:
        return getVibranceOption();
      case PosterEditorItemAdjustmentOptions.CONTRAST:
        return getContrastOption();
      case PosterEditorItemAdjustmentOptions.TINT:
        return getTintOption();
      case PosterEditorItemAdjustmentOptions.MULTIPLY:
        return getMultiplyOption();
      case PosterEditorItemAdjustmentOptions.GAMMA:
        return getGammaOptions();
      case PosterEditorItemAdjustmentOptions.NONE:
      default:
        return getDefaultContent();
    }
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu
        id="adjustments-mobile-options"
        hasMobileMorphingProperties
        includeLeftRightPadding={false}
        contentClassName={`spacing-p-b-0 ${
          selectedSubPanel === PosterEditorItemAdjustmentOptions.NONE ? '' : 'spacing-p-l-4 spacing-p-r-4 spacing-p-b-4'
        } spacing-p-t-1 spacing-m-0`}
      >
        {getContentFromState()}
      </ContextualMenu>
    );
  };

  const getDefaultPanel = (): ReactElement => {
    return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
  };

  const onChangeTintColor = (tintColor: RGBA[], undoable = true): void => {
    void currentPage?.background.updateFromObject(
      {
        details: {
          imageBackgroundItemObject: {
            effects: {
              tintColor: tintColor[0],
            },
          },
        },
      },
      {undoable}
    );
  };
  const onChangeMultiplyColor = (multiplyColor: RGBA[], undoable = true): void => {
    void currentPage?.background.updateFromObject(
      {
        details: {
          imageBackgroundItemObject: {
            effects: {
              multiplyColor: multiplyColor[0],
            },
          },
        },
      },
      {undoable}
    );
  };

  const getTintColorPicker = (): ReactElement | null => {
    if (!currentBackgroundObject || currentBackgroundObject.details.type !== BackgroundTypeName.IMAGE) {
      return null;
    }
    return (
      <PosterEditorColorPanel
        panelId={props.panelId}
        fillObject={{
          fillType: FillTypes.SOLID,
          fillColor: [currentBackgroundObject.details.imageBackgroundItemObject.effects.tintColor],
        }}
        onChangeColor={onChangeTintColor}
        onClose={(): void => {
          props.onClose();
        }}
        onBackButton={(): void => {
          setPanelToShow(AdjustmentPanels.DEFAULT);
        }}
      />
    );
  };
  const getMultiplyColorPicker = (): ReactElement | null => {
    if (!currentBackgroundObject || currentBackgroundObject.details.type !== BackgroundTypeName.IMAGE) {
      return null;
    }
    return (
      <PosterEditorColorPanel
        panelId={props.panelId}
        fillObject={{
          fillType: FillTypes.SOLID,
          fillColor: [currentBackgroundObject.details.imageBackgroundItemObject.effects.multiplyColor],
        }}
        onChangeColor={onChangeMultiplyColor}
        onClose={(): void => {
          props.onClose();
        }}
        onBackButton={(): void => {
          setPanelToShow(AdjustmentPanels.DEFAULT);
        }}
      />
    );
  };

  const getPanels = (): ReactElement | null => {
    switch (panelToShow) {
      case AdjustmentPanels.DEFAULT:
        return getDefaultPanel();
      case AdjustmentPanels.TINT_COLOR_PICKER:
        return getTintColorPicker();
      default:
        return getMultiplyColorPicker();
    }
  };

  return getPanels();
}
